import { useState } from 'react';

import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@nesso/ui/primitives/card';

import { PlanBillingUsageHistoryBar } from './plan-billing-usage-history-card/plan-billing-usage-history-bar';
import {
    DEFAULT_FILTER_VALUE,
    PlanBillingUsageHistoryFilter,
    type UsageHistoryFilterValue,
} from './plan-billing-usage-history-card/plan-billing-usage-history-filter';
import { PlanBillingUsageHistoryGrid } from './plan-billing-usage-history-card/plan-billing-usage-history-grid';

export type UsageHistoryItem = {
    /** Billing-cycle label, e.g. "Sep 3 – Oct 2, 2025". */
    periodLabel: string;
    /** Total sessions used in the cycle. */
    sessions: number;
    /** Cycle session limit — drives the sage/orchid overage split; null = unlimited cycle. */
    included: number | null;
};

/** Largest bar fills this fraction of the width, leaving room for the value label. */
const BAR_MAX_WIDTH_RATIO = 0.85;

type PlanBillingUsageHistoryCardProps = {
    items: UsageHistoryItem[];
    /** Per NSS-470, the time filter only appears from the 7th billing cycle onward. */
    showTimeFilter?: boolean;
};

export function PlanBillingUsageHistoryCard({
    items,
    showTimeFilter = false,
}: PlanBillingUsageHistoryCardProps) {
    const [timeFilter, setTimeFilter] = useState<UsageHistoryFilterValue>(DEFAULT_FILTER_VALUE);

    // Per NSS-470, usage history is not displayed during the first billing cycle.
    if (items.length === 0) {
        return null;
    }

    // The full history is fetched once (newest-first, one per billing cycle); "Last N
    // billing cycles" is the first N items, sliced client-side so filtering is instant.
    // Scale reflects only the visible bars.
    const visibleItems = showTimeFilter ? items.slice(0, timeFilter) : items;
    const maxValue = Math.max(...visibleItems.map((item) => item.sessions));
    const scale = maxValue > 0 ? maxValue / BAR_MAX_WIDTH_RATIO : 0;

    return (
        <Card className="shadow-none mb-8">
            <CardHeader>
                <CardTitle className="text-base font-bold">Usage History</CardTitle>
                {showTimeFilter && (
                    <CardAction>
                        <PlanBillingUsageHistoryFilter
                            value={timeFilter}
                            onValueChange={setTimeFilter}
                        />
                    </CardAction>
                )}
            </CardHeader>
            <CardContent>
                {/* The decorative grid overlays the rows (absolute inset-0, drawn after
                    them), so bar fills and labels lift above its lines with z-10. */}
                <div className="relative">
                    <div className="flex flex-col gap-2">
                        {visibleItems.map((item) => (
                            <PlanBillingUsageHistoryBar
                                key={item.periodLabel}
                                periodLabel={item.periodLabel}
                                sessions={item.sessions}
                                included={item.included}
                                scale={scale}
                            />
                        ))}
                    </div>
                    <PlanBillingUsageHistoryGrid />
                </div>
            </CardContent>
        </Card>
    );
}
